"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Paper,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Divider,
} from "@mui/material"
import { motion } from "framer-motion"
import { useNotification } from "../../contexts/NotificationContext"
import { useAuth } from "../../contexts/AuthContext"
import PageHeader from "../../components/common/PageHeader"
import LoadingButton from "../../components/common/LoadingButton"
import { createUser } from "../../services/user.service"
import { getActiveOrganizations } from "../../services/organization.service"

const CreateUser = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { user, selectedOrganization } = useAuth()
  const [loading, setLoading] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [error, setError] = useState(null)

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "user",
    organizationId: selectedOrganization?._id || "",
    sendWelcomeEmail: true,
  })

  // Form validation
  const [formErrors, setFormErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    organizationId: "",
  })

  const isSuperAdmin = user?.role === "superadmin"

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOrganizations()
    }
  }, [isSuperAdmin])

  const fetchOrganizations = async () => {
    try {
      const response = await getActiveOrganizations()
      setOrganizations(response.data)
    } catch (error) {
      setError("Failed to fetch organizations. Please try again.")
      showNotification("Failed to fetch organizations", "error")
    }
  }

  const handleChange = (e) => {
    const { name, value, checked } = e.target

    // For checkbox inputs
    if (name === "sendWelcomeEmail") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }))
      return
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }))
    }
  }

  const validateForm = () => {
    let isValid = true
    const errors = {}

    // Validate first name
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required"
      isValid = false
    }

    // Validate email
    if (!formData.email.trim()) {
      errors.email = "Email is required"
      isValid = false
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid"
      isValid = false
    }

    // Validate password match if provided
    if (formData.password && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match"
      isValid = false
    }

    // Validate organization for superadmin
    if (isSuperAdmin && !formData.organizationId) {
      errors.organizationId = "Organization is required"
      isValid = false
    }

    setFormErrors(errors)
    return isValid
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)
    setError(null)

    // Prepare userData
    const userData = { ...formData }
    delete userData.confirmPassword

    try {
      const response = await createUser(userData)
      showNotification("User created successfully", "success")
      navigate(`/users/${response.data._id}`)
    } catch (error) {
      console.error("Error creating user:", error)
      setError(error.response?.data?.message || "Failed to create user. Please try again.")
      showNotification(error.response?.data?.message || "Failed to create user", "error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <PageHeader
        title="Create User"
        subtitle="Add a new user to your organization"
        breadcrumbs={[
          { text: "Dashboard", link: "/dashboard" },
          { text: "Users", link: "/users" },
          { text: "Create User" },
        ]}
      />

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          mb: 4,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          User Details
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                error={!!formErrors.password}
                helperText={formErrors.password || "Leave blank to generate a random password"}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!formErrors.confirmPassword}
                helperText={formErrors.confirmPassword}
                variant="outlined"
                disabled={!formData.password}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.sendWelcomeEmail}
                    onChange={handleChange}
                    name="sendWelcomeEmail"
                    color="primary"
                  />
                }
                label="Send welcome email with login details"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.role}>
                <InputLabel id="role-label">Role</InputLabel>
                <Select labelId="role-label" name="role" value={formData.role} onChange={handleChange} label="Role">
                  <MenuItem value="user">Read Only</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  {isSuperAdmin && <MenuItem value="superadmin">Super Admin</MenuItem>}
                </Select>
                <FormHelperText>{formErrors.role || "Select the user's permission level"}</FormHelperText>
              </FormControl>
            </Grid>

            {isSuperAdmin && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!formErrors.organizationId} required>
                  <InputLabel id="organization-label">Organization</InputLabel>
                  <Select
                    labelId="organization-label"
                    name="organizationId"
                    value={formData.organizationId}
                    onChange={handleChange}
                    label="Organization"
                  >
                    {organizations.map((org) => (
                      <MenuItem key={org._id} value={org._id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{formErrors.organizationId || "Select the user's organization"}</FormHelperText>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button variant="outlined" onClick={() => navigate("/users")} sx={{ px: 3 }}>
                Cancel
              </Button>

              <LoadingButton type="submit" loading={loading} variant="contained" color="primary" sx={{ px: 4 }}>
                Create User
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </motion.div>
  )
}

export default CreateUser

